import { NgFor, NgIf } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '../../core/api.service';
import { AuthService } from '../../core/auth.service';
import { dataLocalParaInput, LocalDatePipe } from '../../core/date-utils';
import { Cliente, ConferenciaEntrada, NotaTransferencia, Produto, TAMANHOS } from '../../core/models';

type ConferenciaEntradaFormItem = { id_produto: number; tamanho: string; quantidade: number };

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, NgFor, NgIf, LocalDatePipe],
  template: `
    <header class="page-header">
      <div><h2>Conferencia de entrada</h2><p>Recebimento fisico do estoque do cliente</p></div>
      <button type="button" (click)="novaConferencia()">Nova conferencia</button>
    </header>

    <nav class="tabs" aria-label="Abas de conferencia de entrada">
      <button class="tab-button" type="button" [class.active]="abaAtiva === 'consulta'" (click)="selecionarAba('consulta')">Consulta</button>
      <button class="tab-button" type="button" [class.active]="abaAtiva === 'cadastro'" (click)="selecionarAba('cadastro')">Cadastro e edicao</button>
    </nav>

    <p class="error" *ngIf="erro">{{ erro }}</p>
    <p class="success" *ngIf="mensagem">{{ mensagem }}</p>

    <section class="panel" *ngIf="abaAtiva === 'consulta'">
      <form class="grid-form" [formGroup]="filtros" (ngSubmit)="carregar()">
        <label>Cliente
          <select formControlName="id_cliente">
            <option value="">Todos</option>
            <option *ngFor="let cliente of clientes" [value]="cliente.id_cliente">{{ cliente.nome }}</option>
          </select>
        </label>
        <label>Faccao<input formControlName="faccao"></label>
        <label>Data inicial<input type="date" formControlName="data_inicial"></label>
        <label>Data final<input type="date" formControlName="data_final"></label>
        <label>Status
          <select formControlName="status">
            <option value="">Todos</option>
            <option>ABERTA</option>
            <option>CONFERIDA</option>
            <option>CANCELADA</option>
          </select>
        </label>
        <button type="submit">Pesquisar</button>
      </form>
    </section>

    <table class="data-table" *ngIf="abaAtiva === 'consulta'">
      <thead>
        <tr><th>Conferencia</th><th>Cliente</th><th>Faccao</th><th>Produto</th><th>Recebimento</th><th>Conferencia</th><th>Total</th><th>Status</th><th></th></tr>
      </thead>
      <tbody>
        <tr *ngFor="let conferencia of conferencias">
          <td data-label="Codigo">{{ conferencia.id_conferencia }}</td>
          <td data-label="Cliente">{{ conferencia.cliente }}</td>
          <td data-label="Faccao">{{ conferencia.faccao }}</td>
          <td data-label="Produto">{{ conferencia.produtos_resumo || conferencia.codigo_produto || '-' }}</td>
          <td data-label="Recebimento">{{ conferencia.data_recebimento | localDate }}</td>
          <td data-label="Conferencia">{{ conferencia.data_conferencia | localDate }}</td>
          <td data-label="Total">{{ conferencia.total_pecas }}</td>
          <td data-label="Status"><span class="status" [class.finalizada]="conferencia.status === 'CONFERIDA'" [class.cancelada]="conferencia.status === 'CANCELADA'">{{ conferencia.status }}</span></td>
          <td class="actions" data-label="Acoes">
            <button type="button" (click)="editar(conferencia)" [disabled]="conferencia.status !== 'ABERTA'">Editar</button>
            <button type="button" (click)="finalizar(conferencia)" [disabled]="conferencia.status !== 'ABERTA'">Finalizar</button>
            <button type="button" (click)="pdf(conferencia)">PDF</button>
            <button class="ghost" type="button" (click)="cancelar(conferencia)" [disabled]="conferencia.status === 'CANCELADA'">Cancelar</button>
          </td>
        </tr>
      </tbody>
    </table>

    <section class="panel" *ngIf="abaAtiva === 'cadastro'">
      <form [formGroup]="form" (ngSubmit)="salvar(false)" novalidate>
        <div class="grid-form">
          <input type="hidden" formControlName="id_conferencia">
          <input type="hidden" formControlName="id_nota">
          <label>Cliente
            <select formControlName="id_cliente">
              <option value="">Selecione</option>
              <option *ngFor="let cliente of clientes" [value]="cliente.id_cliente">{{ cliente.nome }}</option>
            </select>
          </label>
          <label>Faccao<input formControlName="faccao"></label>
          <label>Responsavel<input [value]="auth.usuario?.nome || 'Usuario logado'" disabled></label>
          <label>Recebimento<input type="date" formControlName="data_recebimento"></label>
          <label>Data conferencia<input type="date" formControlName="data_conferencia"></label>
          <label>Cor<input formControlName="cor"></label>
          <label class="full">Observacao<textarea rows="2" formControlName="observacao"></textarea></label>
        </div>

        <div class="inline-picker">
          <label>Produto
            <select formControlName="produto_para_adicionar">
              <option value="">Selecione</option>
              <option *ngFor="let produto of produtosDaOrdem" [value]="produto.cod_produto">{{ produto.codigo_produto }} - {{ produto.descricao }}</option>
            </select>
          </label>
          <label>Tamanho
            <select formControlName="tamanho_para_adicionar">
              <option value="">Selecione</option>
              <option *ngFor="let tamanho of tamanhosDisponiveis" [value]="tamanho">{{ tamanho }}</option>
            </select>
          </label>
          <button class="ghost" type="button" (click)="adicionarItem()" [disabled]="!form.controls.produto_para_adicionar.value || !form.controls.tamanho_para_adicionar.value">Adicionar item</button>
        </div>

        <table class="size-table" formArrayName="itens" *ngIf="itens.length">
          <thead><tr><th>Produto</th><th>Tamanho</th><th>Quantidade recebida</th><th></th></tr></thead>
          <tbody>
            <tr *ngFor="let item of itens.controls; let i = index" [formGroupName]="i">
              <td data-label="Produto">{{ produtoLabel(item.value.id_produto) }}</td>
              <td data-label="Tamanho">{{ item.value.tamanho }}</td>
              <td data-label="Quantidade recebida"><input type="number" min="1" formControlName="quantidade"></td>
              <td class="actions" data-label="Acoes"><button class="ghost" type="button" (click)="removerItem(i)">Remover</button></td>
            </tr>
          </tbody>
        </table>
        <p class="field-hint" *ngIf="!itens.length">Nenhum item selecionado</p>

        <div class="total-line">Total recebido: <strong>{{ total }}</strong> pecas</div>

        <div class="form-actions">
          <button type="submit" [disabled]="form.invalid">Salvar conferencia</button>
          <button type="button" (click)="salvar(true)" [disabled]="form.invalid">Finalizar conferencia</button>
          <button class="ghost" type="button" (click)="limpar()">Limpar</button>
          <button class="ghost" type="button" (click)="abaAtiva = 'consulta'">Voltar</button>
        </div>
      </form>
    </section>
  `
})
export class ConferenciasEntradaComponent implements OnInit {
  private api = inject(ApiService);
  auth = inject(AuthService);
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);

  clientes: Cliente[] = [];
  produtos: Produto[] = [];
  ordens: NotaTransferencia[] = [];
  ordemSelecionada?: NotaTransferencia;
  conferencias: ConferenciaEntrada[] = [];
  erro = '';
  mensagem = '';
  abaAtiva: 'consulta' | 'cadastro' = 'consulta';

  filtros = this.fb.nonNullable.group({
    id_cliente: [''],
    faccao: [''],
    data_inicial: [''],
    data_final: [''],
    status: ['']
  });

  form = this.fb.nonNullable.group({
    id_conferencia: [0],
    id_nota: [''],
    id_cliente: ['', Validators.required],
    faccao: ['', Validators.required],
    data_recebimento: [dataLocalParaInput(), Validators.required],
    data_conferencia: [dataLocalParaInput(), Validators.required],
    cor: [''],
    observacao: [''],
    produto_para_adicionar: [''],
    tamanho_para_adicionar: [''],
    itens: this.fb.array([])
  });

  get ordensDisponiveis() {
    return this.ordens.filter((ordem) => !['CONFERIDA', 'DIVERGENTE', 'FINALIZADA', 'CANCELADA'].includes(ordem.status));
  }

  get produtosDaOrdem() {
    if (!this.ordemSelecionada) return this.produtos.filter((produto) => produto.ativo);
    if (String(this.ordemSelecionada.numero_nota || '').startsWith('ESTOQUE-')) {
      return this.produtos.filter((produto) => produto.ativo);
    }
    const ids = new Set((this.ordemSelecionada.itens || []).map((item) => Number(item.id_produto || 0)).filter(Boolean));
    return this.produtos.filter((produto) => produto.ativo && ids.has(produto.cod_produto));
  }

  get itens() {
    return this.form.controls.itens as FormArray;
  }

  get total() {
    return this.itens.controls.reduce((sum, control) => sum + Number(control.value.quantidade || 0), 0);
  }

  get tamanhosDisponiveis() {
    const idProduto = Number(this.form.controls.produto_para_adicionar.value || 0);
    const selecionados = this.itens.controls
      .filter((control) => Number(control.value.id_produto || 0) === idProduto)
      .map((control) => control.value.tamanho);

    return TAMANHOS.filter((tamanho) => !selecionados.includes(tamanho));
  }

  ngOnInit() {
    this.api.clientes().subscribe((clientes) => (this.clientes = clientes.filter((cliente) => cliente.ativo)));
    this.api.produtos().subscribe((produtos) => (this.produtos = produtos.filter((produto) => produto.ativo)));
    this.api.notas().subscribe((ordens) => {
      this.ordens = ordens;
      const idOrdem = this.route.snapshot.queryParamMap.get('ordem');
      if (idOrdem) {
        this.form.patchValue({ id_nota: idOrdem });
        this.selecionarOrdem();
        this.abaAtiva = 'cadastro';
        return;
      }

      const idCliente = this.route.snapshot.queryParamMap.get('cliente');
      if (idCliente) {
        this.form.patchValue({ id_cliente: idCliente });
        this.abaAtiva = 'cadastro';
      }
    });
    this.carregar();
  }

  carregar() {
    this.api.conferenciasEntrada(this.filtros.getRawValue()).subscribe((conferencias) => (this.conferencias = conferencias));
  }

  selecionarAba(aba: 'consulta' | 'cadastro') {
    this.abaAtiva = aba;
    this.erro = '';
    this.mensagem = '';
  }

  novaConferencia() {
    this.limpar();
    this.abaAtiva = 'cadastro';
  }

  selecionarOrdem(limparItens = true, afterLoad?: () => void) {
    const idOrdem = Number(this.form.controls.id_nota.value || 0);
    this.ordemSelecionada = undefined;
    this.form.controls.produto_para_adicionar.setValue('');
    this.form.controls.tamanho_para_adicionar.setValue('');
    if (limparItens) this.itens.clear();
    if (!idOrdem) return;

    this.api.nota(idOrdem).subscribe((ordem) => {
      this.ordemSelecionada = ordem;
      this.form.patchValue({
        id_cliente: String(ordem.id_cliente),
        data_recebimento: String(ordem.data_recebimento || dataLocalParaInput()).slice(0, 10),
        cor: ordem.cor || ''
      });
      afterLoad?.();
    });
  }

  salvar(finalizar: boolean) {
    this.erro = '';
    this.mensagem = '';
    this.form.markAllAsTouched();

    const raw = this.form.getRawValue();
    const itens = raw.itens as ConferenciaEntradaFormItem[];
    if (this.form.invalid || this.total <= 0) {
      this.erro = 'Informe cliente, faccao, produto, tamanho e quantidade';
      return;
    }

    const payload = {
      ...raw,
      id_conferencia: raw.id_conferencia || undefined,
      id_nota: raw.id_nota ? Number(raw.id_nota) : undefined,
      id_cliente: Number(raw.id_cliente),
      id_produto: itens[0]?.id_produto,
      produto_para_adicionar: undefined,
      tamanho_para_adicionar: undefined,
      itens: itens
        .map((item) => ({
          id_produto: Number(item.id_produto || 0),
          tamanho: item.tamanho,
          quantidade: Number(item.quantidade || 0)
        }))
        .filter((item) => item.quantidade > 0)
    };

    this.api.salvarConferenciaEntrada(payload).subscribe({
      next: (conferencia) => {
        if (finalizar) {
          this.api.finalizarConferenciaEntrada(conferencia.id_conferencia).subscribe({
            next: () => this.voltarParaConsulta('Conferencia finalizada com sucesso'),
            error: (error) => (this.erro = error.error?.message || 'Erro ao finalizar conferencia')
          });
          return;
        }

        this.voltarParaConsulta('Conferencia salva com sucesso');
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao salvar conferencia')
    });
  }

  editar(conferencia: ConferenciaEntrada) {
    this.api.conferenciaEntrada(conferencia.id_conferencia).subscribe((detalhe) => {
      this.form.patchValue({
        id_conferencia: detalhe.id_conferencia,
        id_nota: detalhe.id_nota ? String(detalhe.id_nota) : '',
        id_cliente: String(detalhe.id_cliente),
        faccao: detalhe.faccao || '',
        data_recebimento: String(detalhe.data_recebimento).slice(0, 10),
        data_conferencia: String(detalhe.data_conferencia).slice(0, 10),
        cor: detalhe.cor || '',
        observacao: detalhe.observacao || '',
        produto_para_adicionar: '',
        tamanho_para_adicionar: ''
      });

      this.selecionarOrdem(false, () => {
        this.itens.clear();
        detalhe.itens
          ?.filter((item) => Number(item.quantidade || 0) > 0)
          .forEach((item) => this.itens.push(this.itemGroup(Number(item.id_produto || 0), item.tamanho, Number(item.quantidade || 0))));
      });

      this.erro = '';
      this.mensagem = '';
      this.abaAtiva = 'cadastro';
    });
  }

  finalizar(conferencia: ConferenciaEntrada) {
    this.api.finalizarConferenciaEntrada(conferencia.id_conferencia).subscribe(() => this.voltarParaConsulta('Conferencia finalizada com sucesso'));
  }

  cancelar(conferencia: ConferenciaEntrada) {
    const confirmar = window.confirm(`Cancelar a conferencia ${conferencia.id_conferencia}?`);
    if (!confirmar) return;

    this.api.cancelarConferenciaEntrada(conferencia.id_conferencia).subscribe(() => this.voltarParaConsulta('Conferencia cancelada'));
  }

  pdf(conferencia: ConferenciaEntrada) {
    this.api.abrirConferenciaEntrada(conferencia.id_conferencia);
  }

  limpar() {
    this.erro = '';
    this.mensagem = '';
    this.ordemSelecionada = undefined;
    this.form.reset({
      id_conferencia: 0,
      id_nota: '',
      id_cliente: '',
      faccao: '',
      data_recebimento: dataLocalParaInput(),
      data_conferencia: dataLocalParaInput(),
      cor: '',
      observacao: '',
      produto_para_adicionar: '',
      tamanho_para_adicionar: ''
    });
    this.itens.clear();
  }

  adicionarItem() {
    const idProduto = Number(this.form.controls.produto_para_adicionar.value || 0);
    const tamanho = this.form.controls.tamanho_para_adicionar.value;
    if (!idProduto || !tamanho) return;
    if (this.itens.controls.some((item) => Number(item.value.id_produto || 0) === idProduto && item.value.tamanho === tamanho)) return;

    this.itens.push(this.itemGroup(idProduto, tamanho, 1));
    this.form.controls.tamanho_para_adicionar.setValue('');
  }

  removerItem(index: number) {
    this.itens.removeAt(index);
  }

  produtoLabel(idProduto: number) {
    const produto = this.produtos.find((item) => item.cod_produto === Number(idProduto || 0));
    return produto ? `${produto.codigo_produto} - ${produto.descricao}` : idProduto;
  }

  private voltarParaConsulta(mensagem: string) {
    this.limpar();
    this.mensagem = mensagem;
    this.abaAtiva = 'consulta';
    this.carregar();
  }

  private itemGroup(idProduto: number, tamanho: string, quantidade: number) {
    return this.fb.nonNullable.group({
      id_produto: [idProduto],
      tamanho: [tamanho],
      quantidade: [quantidade, [Validators.required, Validators.min(1)]]
    });
  }
}
